import * as React from 'react';
import User from '@app/Interface/UserInterface';
import NotificationInterface from '@app/Interface/NotificationInterface';
import { Dropdown, DropdownToggle, PageHeaderToolsItem } from '@patternfly/react-core';
import { BellIcon, AttentionBellIcon } from '@patternfly/react-icons';
import NotificationItems from "@app/Components/PageHeader/NotificationItems";
import ProducerInterface from '@app/Interface/ProducerInterface';
import ProducerHelper from '@app/Service/ProducerHelper';
import PageInterface from '@app/Interface/PageInterface';
import PageHelper from '@app/Service/PageHelper';
import TemplateDefinitions from '@app/utils/TemplateDefinitions';
import ItemLoader from '../ItemLoader';

interface NotificationMenuProps {
    currentUser: User;
    notifications: NotificationInterface[];
    onFpuChange: (fpu: ProducerInterface) => void;
}

const NotificationMenu: React.FC<NotificationMenuProps> = ({ currentUser, notifications, onFpuChange }) => {

    const dropdownId = "notification-menu-id";

    const hasNotificationColor = currentUser.isMember ? "var(--pf-global--info-color--100)" : "var(--pf-global--danger-color--100)";

    const initialProducers: ProducerInterface[] = [];

    const [producers, setProducers] = React.useState(initialProducers);

    const [isOpen, setIsOpen] = React.useState(false);

    const [dropdownItems, setDropdownItems] = React.useState<JSX.Element[]>([]);

    const [chartPages, setChartPages] = React.useState<PageInterface[]>([]);

    const [producersLoading, setProducersLoading] = React.useState(true);

    const [pagesLoading, setPagesLoading] = React.useState(true);

    const [isLoading, setIsLoading] = React.useState(true);

    const [hasNotifications, setHasNotifications] = React.useState(false);

    const onToggle = (isOpen: boolean) => {

        setIsOpen(isOpen);

    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onSelect = (_event?: React.SyntheticEvent<HTMLDivElement> | undefined) => {

        setIsOpen(!isOpen);

        onFocus();

    }

    const onFocus = () => {

        const element = document.getElementById(dropdownId);

        element?.focus;

    }

    // check if there's at least one notification, this determines color
    React.useEffect(() => {

        const hasNotifications = notifications.some(notification => notification.dismissed === false);

        setHasNotifications(hasNotifications);

    }, [notifications]);

    // get a list of producers for the logged in user
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        const getUserProducers = async () => {

            const producers = await ProducerHelper.getProducers(controller);

            if (mounted) {
                setProducers(producers);

                setProducersLoading(false);
            }

        }

        setProducersLoading(true);

        getUserProducers();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // get a list of chart pages to generate links
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        const getChartPages = async () => {

            const pages = await PageHelper.getAllPagesByTemplateName(controller, currentUser.currentFpuId, TemplateDefinitions.TEMPLATE_CHART_PAGE);

            if (mounted) {
                setChartPages(pages);

                setPagesLoading(false);
            }

        }

        setPagesLoading(true);

        getChartPages();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [currentUser.currentFpuId]);

    // create notification items 
    React.useEffect(() => {

        if (producers.length) {

            const items = [<NotificationItems key="notifications-group-1" currentUser={currentUser} notifications={notifications} producers={producers} onFpuChange={onFpuChange} chartPages={chartPages} />];

            setDropdownItems(items);

        }

    }, [chartPages, currentUser, notifications, onFpuChange, producers]);

    // set isLoading
    React.useEffect(() => {

        const loading = producersLoading || pagesLoading || dropdownItems.length === 0;

        setIsLoading(loading);

    }, [dropdownItems.length, pagesLoading, producersLoading]);

    if (isLoading) {

        const loader = [<ItemLoader key="item-load-1" />];

        return (
            <PageHeaderToolsItem className="pf-u-mx-xs">
                <Dropdown
                    isOpen={isOpen}
                    isPlain
                    position="right"
                    toggle={
                        <DropdownToggle
                            id={dropdownId}
                            onToggle={onToggle}
                            toggleIndicator={null}
                            icon={<BellIcon size="md" title="Loading..." color="black" />}
                        />
                    }
                    dropdownItems={loader}
                />
            </PageHeaderToolsItem>
        );

    }

    return (
        <PageHeaderToolsItem className="pf-u-mx-xs">
            <Dropdown
                onSelect={onSelect}
                isOpen={isOpen}
                isPlain
                alignments={{sm: 'left', md: 'left', lg: 'right'}}
                toggle={
                    <DropdownToggle
                        id={dropdownId}
                        onToggle={onToggle}
                        toggleIndicator={null}
                        icon={hasNotifications ? <AttentionBellIcon size="md" title="View Notifications" color="white" /> : <BellIcon size="md" title="No Notifications" color="black" />  }
                    />
                }
                dropdownItems={dropdownItems}
            />
        </PageHeaderToolsItem>
    );


}

export default NotificationMenu;